// src/context/ProductContext.js
import { createContext, useContext, useEffect, useState } from "react";
import { apiClient } from "../services/apiClientService";

const ProductContext = createContext();

export const useProducts = () => useContext(ProductContext);

export const ProductProvider = ({ children }) => {
  const [products, setProducts] = useState([]); // paginated products
  const [filtered, setFiltered] = useState([]);
  const [allProducts, setAllProducts] = useState([]); // used only for search suggestions
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [fetched, setFetched] = useState(false);

  const fetchProducts = async ({ page = 1, limit = 5 } = {}) => {
    try {
      setLoading(true);
      const res = await apiClient("GET", "/api/products", null, {
        skipLoading: true,
        params: { page, limit },
      });

      const list = Array.isArray(res?.products) ? res.products : [];
      setProducts(list);
      setFiltered(list);
      setTotalPages(res?.totalPages ?? 1);
      setCurrentPage(res?.currentPage ?? 1);
    } catch (err) {
      console.error("Error fetching paginated products:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllProducts = async () => {
    try {
      setLoading(true);
      const res = await apiClient("GET", "/api/products", null, {
        skipLoading: true,
      });
      const list = Array.isArray(res?.products) ? res.products : [];
      setProducts(list);
      setFiltered(list);
      setAllProducts(list);
      setFetched(true);
    } catch (err) {
      console.error("Failed to fetch products:", err.message);
    } finally {
      setLoading(false);
    }
  };

  const filterProducts = (query) => {
    const { type, keyword, saleType, minPrice, maxPrice } = query;

    const result = products.filter((p) => {
      const matchesType = type ? p.type.includes(type) : true;
      const matchesKeyword = keyword
        ? p.name.toLowerCase().includes(keyword.toLowerCase())
        : true;
      const matchesSaleType = saleType ? p.saleType === saleType : true;
      const matchesMinPrice = minPrice
        ? p.discountedPrice >= Number(minPrice)
        : true;
      const matchesMaxPrice = maxPrice
        ? p.discountedPrice <= Number(maxPrice)
        : true;

      return (
        matchesType &&
        matchesKeyword &&
        matchesSaleType &&
        matchesMinPrice &&
        matchesMaxPrice
      );
    });

    setFiltered(result);
  };

  useEffect(() => {
    if (!fetched) {
      fetchAllProducts();
    }
  }, [fetched]); // keep dependency as fetched

  return (
    <ProductContext.Provider
      value={{
        allProducts,
        products: filtered,
        loading,
        reloadProducts: fetchProducts,
        filterProducts,
        currentPage,
        totalPages,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};
